import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Play } from "lucide-react";
import { resolveUrl, isUploadedVideo, getEmbedUrl, mediaItems } from "../../lib/media";
import { frameStyle, frameAspectClass } from "../../lib/frame";
import { FramedImage } from "./FramedImage";
import { Lightbox } from "./PhotoMasonry";

// Global defaults — preserve historic behaviour when no per-gallery settings exist.
export const GALLERY_DEFAULTS = { mode: "fade", autoplay: true, intervalSec: 5, arrows: true, dots: true, swipe: true, loop: true, pauseOnHover: true, autoplayVideo: true };

// One media slide (photo or video). Photos open the lightbox; videos play inline.
// When `autoStart` is on, the video of the ACTIVE slide plays automatically, muted.
const Slide = ({ m, active, autoStart, onImageClick, onImgLoad, imgFit = "object-contain", imgStyle }) => {
  const vref = useRef(null);
  useEffect(() => {
    const el = vref.current;
    if (!el) return;
    if (active && autoStart) { el.muted = true; el.play().catch(() => {}); }
    else { el.pause(); try { el.currentTime = 0; } catch { /* noop */ } }
  }, [active, autoStart]);

  if (m.type === "video") {
    if (isUploadedVideo(m.url)) {
      return (
        <video ref={vref} src={resolveUrl(m.url)} poster={resolveUrl(m.poster) || undefined} controls playsInline preload="metadata"
          muted={autoStart} data-testid={active ? "gallery-active" : undefined}
          className={`absolute inset-0 w-full h-full object-contain ${m.frame?.noBg ? "" : "bg-black"}`} />
      );
    }
    const embed = getEmbedUrl(m.url);
    const src = active && autoStart ? `${embed}${embed.includes("?") ? "&" : "?"}autoplay=1&mute=1` : embed;
    return (
      <iframe src={src} title="Vidéo" loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
        data-testid={active ? "gallery-active" : undefined}
        className="absolute inset-0 w-full h-full bg-black" />
    );
  }
  return (
    <img src={resolveUrl(m.url)} alt="" onClick={onImageClick} onLoad={onImgLoad} onError={onImgLoad} decoding="async"
      fetchpriority={active ? "high" : "low"} data-testid={active ? "gallery-active" : undefined}
      style={m.frame ? frameStyle(m.frame, "cover") : imgStyle}
      className={`absolute inset-0 w-full h-full ${m.frame ? "" : imgFit} cursor-zoom-in transition-opacity duration-700`} />
  );
};

// Reusable UNIFIED gallery — photos AND videos in one carousel.
// Accepts legacy props (mode / autoplay) or a full `options` object, and a mixed
// media array (URL strings = photos, or { type, url, poster } objects).
// mode: "fixed" (grid) | "slide" (horizontal) | "vertical" | "fade" | "manual"
export const SmartGallery = ({ images = [], mode, autoplay, options, label = "Galerie", frameClass = "aspect-video", imgFit = "object-contain", imgStyle }) => {
  const media = mediaItems(images);
  const imgs = media.filter((m) => m.type === "image").map((m) => m.url); // lightbox = photos only
  const s = { ...GALLERY_DEFAULTS };
  if (mode !== undefined) s.mode = mode;
  if (autoplay !== undefined) s.autoplay = autoplay;
  if (options && typeof options === "object") Object.assign(s, options);

  const [i, setI] = useState(0);        // cible demandée (flèches, points, autoplay)
  const [shown, setShown] = useState(0); // slide réellement affichée (garantie chargée)
  const [box, setBox] = useState(null);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const touch = useRef({ x: 0, y: 0, active: false });
  const loaded = useRef(new Set());      // URLs d'images déjà téléchargées (jamais rechargées)
  const target = useRef(0);

  const vertical = s.mode === "vertical";
  const animated = s.mode !== "manual" && s.mode !== "fixed";
  const onVideoSlide = media[shown]?.type === "video"; // autoplay pauses on video slides

  const markLoaded = useCallback((u) => { if (u) loaded.current.add(u); }, []);

  // Bascule l'affichage vers la cible seulement quand l'image est téléchargée :
  // l'image courante reste visible pendant le chargement (jamais de cadre vide).
  useEffect(() => {
    target.current = i;
    const m = media[i];
    if (!m) return;
    if (m.type === "video") { setShown(i); return; }
    const u = m.url;
    if (!u || loaded.current.has(u)) { setShown(i); return; }
    let alive = true;
    const im = new Image();
    im.onload = im.onerror = () => { markLoaded(u); if (alive && target.current === i) setShown(i); };
    im.src = resolveUrl(u);
    const t = setTimeout(() => { if (alive && target.current === i) setShown(i); }, 5000); // garde-fou connexion lente
    return () => { alive = false; clearTimeout(t); };
  }, [i, media, markLoaded]);

  const go = useCallback((n) => setI((x) => {
    if (media.length < 2) return 0;
    if (s.loop) return (n + media.length) % media.length;
    return Math.max(0, Math.min(media.length - 1, n));
  }), [media.length, s.loop]);

  const openLightbox = (mi) => { const idx = imgs.indexOf(media[mi]?.url); if (idx >= 0) setBox(idx); };

  const onTouchStart = (e) => { if (!s.swipe) return; touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, active: true }; };
  const onTouchEnd = (e) => {
    if (!s.swipe || !touch.current.active || media.length < 2) return;
    const d = vertical ? e.changedTouches[0].clientY - touch.current.y : e.changedTouches[0].clientX - touch.current.x;
    if (Math.abs(d) > 40) go(i + (d < 0 ? 1 : -1));
    touch.current.active = false;
  };

  useEffect(() => {
    if (s.mode === "fixed" || !s.autoplay || media.length < 2 || box !== null || paused || onVideoSlide) return;
    const ms = (Number(s.intervalSec) || 5) * 1000;
    timer.current = setInterval(() => setI((x) => (x + 1) % media.length), ms);
    return () => clearInterval(timer.current);
  }, [s.mode, s.autoplay, s.intervalSec, media.length, box, paused, onVideoSlide]);

  if (!media.length) return null;

  // FIXED: responsive grid of all media
  if (s.mode === "fixed") {
    return (
      <div data-testid="smart-gallery" data-mode="fixed">
        {label && <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">{label}</h2>}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {media.map((m, x) => {
            const bf = m.frame || {};
            const itemAspect = bf.format && bf.format !== "auto" ? frameAspectClass(bf, "aspect-[4/3]") : "aspect-[4/3]";
            const boxCls = `overflow-hidden ${bf.border ? "rounded-lg border border-white/15" : "rounded-lg border border-white/10"} ${bf.noBg ? "" : m.type === "video" ? "bg-black" : "bg-[#0e0e0e]"}`;
            if (m.type === "video") {
              return (
                <div key={x} data-testid={`gallery-media-${x}`} className={`${itemAspect || ""} ${boxCls}`}>
                  {isUploadedVideo(m.url)
                    ? <video src={resolveUrl(m.url)} poster={resolveUrl(m.poster) || undefined} controls playsInline preload="metadata" className={`w-full h-full object-contain ${bf.noBg ? "" : "bg-black"}`} />
                    : <iframe src={getEmbedUrl(m.url)} title="Vidéo" loading="lazy" allowFullScreen className="w-full h-full" />}
                </div>
              );
            }
            if (itemAspect === null) {
              // Format « proportions d'origine » : le bloc épouse l'image (jamais recadrée).
              return (
                <button key={x} onClick={() => openLightbox(x)} data-testid={`gallery-media-${x}`} className={`group ${bf.border ? "rounded-lg border border-white/15 overflow-hidden" : ""}`}>
                  <FramedImage src={m.url} frame={bf} alt="" className="group-hover:scale-105 transition-transform duration-500" />
                </button>
              );
            }
            return (
              <button key={x} onClick={() => openLightbox(x)} data-testid={`gallery-media-${x}`} className={`${itemAspect} ${boxCls} group`}>
                <img src={resolveUrl(m.url)} alt="" loading="lazy" style={m.frame ? frameStyle(m.frame, "cover") : undefined} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            );
          })}
        </div>
        <Lightbox images={imgs} index={box} onClose={() => setBox(null)} />
      </div>
    );
  }

  const atStart = !s.loop && i === 0;
  const atEnd = !s.loop && i === media.length - 1;
  // Options d'affichage du bloc issues de la slide ACTIVE (fond, bordure — par média).
  const actFrame = media[shown]?.frame || {};
  // La transition (fondu / translation) joue sur la slide AFFICHÉE (chargée) — la cible suit dès qu'elle est prête.
  const frameCls = (x) => {
    if (s.mode === "fade") return { opacity: x === shown ? 1 : 0, pointerEvents: x === shown ? "auto" : "none" };
    const off = (x - shown) * 100;
    return { transform: vertical ? `translateY(${off}%)` : `translateX(${off}%)` };
  };
  const PrevIcon = vertical ? ChevronUp : ChevronLeft;
  const NextIcon = vertical ? ChevronDown : ChevronRight;
  const arrowBase = "absolute h-10 w-10 rounded-full bg-black/60 backdrop-blur-sm border border-white/25 text-white flex items-center justify-center transition-colors hover:border-electric hover:text-electric hover:bg-black/80 disabled:opacity-30 disabled:cursor-not-allowed z-10";

  return (
    <div data-testid="smart-gallery" data-mode={s.mode}>
      {label && <h2 className="font-serif text-2xl md:text-3xl text-white mb-5">{label}</h2>}
      <div className={`relative w-full ${frameClass} rounded-xl overflow-hidden ${actFrame.border ? "border border-white/25" : "border border-white/10"} ${actFrame.noBg ? "" : "bg-[#0e0e0e]"} group touch-pan-y`}
        onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        onMouseEnter={() => s.pauseOnHover && setPaused(true)} onMouseLeave={() => s.pauseOnHover && setPaused(false)}>
        {media.map((m, x) => (
          <div key={x} className={`absolute inset-0 ${animated ? (s.mode === "fade" ? "transition-opacity duration-700" : "transition-transform duration-500") : ""}`} style={frameCls(x)}>
            <Slide m={m} active={x === shown} autoStart={s.autoplayVideo !== false} onImageClick={() => openLightbox(x)} onImgLoad={() => markLoaded(m.url)} imgFit={imgFit} imgStyle={imgStyle} />
          </div>
        ))}
        {media.length > 1 && s.arrows && (
          <>
            <button onClick={() => go(i - 1)} disabled={atStart} data-testid="gallery-prev" aria-label="Précédent"
              className={`${arrowBase} ${vertical ? "top-3 left-1/2 -translate-x-1/2" : "left-3 top-1/2 -translate-y-1/2"}`}><PrevIcon size={20} /></button>
            <button onClick={() => go(i + 1)} disabled={atEnd} data-testid="gallery-next" aria-label="Suivant"
              className={`${arrowBase} ${vertical ? "bottom-3 left-1/2 -translate-x-1/2" : "right-3 top-1/2 -translate-y-1/2"}`}><NextIcon size={20} /></button>
          </>
        )}
        {media.length > 1 && s.dots && (
          <div className={`absolute flex gap-1.5 z-10 ${vertical ? "right-3 top-1/2 -translate-y-1/2 flex-col" : "bottom-3 left-1/2 -translate-x-1/2"}`}>
            {media.map((m, x) => (
              <button key={x} onClick={() => setI(x)} aria-label={`Média ${x + 1}`}
                className={`rounded-full transition-all flex items-center justify-center ${x === shown ? (vertical ? "h-6 w-1.5" : "w-6 h-1.5") + " bg-electric" : "h-1.5 w-1.5 bg-white/40"}`}>
                {m.type === "video" && x !== shown && <Play size={7} className="text-white/70" />}
              </button>
            ))}
          </div>
        )}
      </div>
      <Lightbox images={imgs} index={box} onClose={() => setBox(null)} />
    </div>
  );
};


// Compact carousel for CARDS (fills its parent). Photos/videos as slides; videos show
// their poster + a play badge (full playback happens on the detail page). Arrows/dots
// stop propagation so a wrapping <Link> still navigates on a normal click.
export const CardMediaCarousel = ({ images = [], settings = {}, className = "" }) => {
  const media = mediaItems(images);
  const s = { ...GALLERY_DEFAULTS, ...(settings || {}) };
  const [i, setI] = useState(0);
  const timer = useRef(null);
  const many = media.length > 1;

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const go = (n) => setI((x) => (n + media.length) % media.length);

  useEffect(() => {
    if (!many || !s.autoplay) return;
    timer.current = setInterval(() => setI((x) => (x + 1) % media.length), (Number(s.intervalSec) || 5) * 1000);
    return () => clearInterval(timer.current);
  }, [many, s.autoplay, s.intervalSec, media.length]);

  if (!media.length) return <div className={`w-full h-full bg-[#0e0e0e] ${className}`} />;

  return (
    <div className={`relative w-full h-full overflow-hidden bg-[#0e0e0e] ${className}`} data-testid="card-media-carousel">
      {media.map((m, x) => (
        <div key={x} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: x === i ? 1 : 0 }}>
          <img src={resolveUrl(m.type === "video" ? (m.poster || "") : m.url) || undefined} alt=""
            style={m.type !== "video" && m.frame ? frameStyle(m.frame, "cover") : undefined}
            className="w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-110" />
          {m.type === "video" && (
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="h-12 w-12 rounded-full bg-black/50 border border-white/40 flex items-center justify-center text-white"><Play size={18} className="ml-0.5" /></span>
            </span>
          )}
        </div>
      ))}
      {many && (
        <>
          <button type="button" onClick={(e) => { stop(e); go(i - 1); }} aria-label="Précédent" data-testid="card-carousel-prev"
            className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/55 border border-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:border-electric hover:text-electric z-10"><ChevronLeft size={16} /></button>
          <button type="button" onClick={(e) => { stop(e); go(i + 1); }} aria-label="Suivant" data-testid="card-carousel-next"
            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/55 border border-white/25 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:border-electric hover:text-electric z-10"><ChevronRight size={16} /></button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {media.map((m, x) => (
              <button key={x} type="button" onClick={(e) => { stop(e); setI(x); }} aria-label={`Média ${x + 1}`}
                className={`rounded-full transition-all ${x === i ? "w-5 h-1.5 bg-electric" : "h-1.5 w-1.5 bg-white/40"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
